import React, { useState, useMemo } from 'react';
import { SxProps, Theme, Box, CircularProgress, Typography } from '@mui/material';
import { ClinicalReviewModel } from '../../../lib/models/ClinicalReviewModel';
import Card from '../../Utils/Card';
import ClinicalReviewListing, { ColKeys } from './ClinicalReviewListing';
import { useScreenLargerThan } from '../../../lib/hooks/breakpoint-hooks';

export type ReviewsListProps = {
    data: ClinicalReviewModel[];
    totalReviews: number;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    defaultSortColumn?: 'dueDate' | 'assignedDate' | 'hasNewMessage';
    defaultOrder?: 'asc' | 'desc';
    loading?: boolean;
    removeColumns?: ColKeys[];
};

const ReviewsList = ({
    totalReviews,
    data,
    title,
    subtitle,
    defaultSortColumn = 'dueDate',
    defaultOrder = 'asc',
    loading = false,
    removeColumns
}: ReviewsListProps) => {
    const isLargerThanSmall = useScreenLargerThan('sm');
    const [page, setPage] = useState(1);
    const [sortColumn, setSortColumn] = useState<'dueDate' | 'assignedDate' | 'hasNewMessage'>(
        defaultSortColumn
    );
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultOrder);
    const itemsPerPage = 10;

    const sortedData = useMemo(() => {
        const dataRef = [...data];

        if (sortColumn === 'hasNewMessage') {
            return dataRef.sort((a, b) => {
                if (a.hasNewMessage === b.hasNewMessage) return 0;

                if (sortDirection === 'asc') {
                    return a.hasNewMessage ? 1 : -1;
                }

                return a.hasNewMessage ? -1 : 1;
            });
        }

        if (sortColumn === 'dueDate' || sortColumn === 'assignedDate') {
            return dataRef.sort((a, b) => {
                const dateA = new Date(a[sortColumn]);
                const dateB = new Date(b[sortColumn]);
                return sortDirection === 'asc'
                    ? dateA.getTime() - dateB.getTime()
                    : dateB.getTime() - dateA.getTime();
            });
        }

        return dataRef;
    }, [data, sortColumn, sortDirection]);

    const pagedData = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, page]);

    const pageCount = Math.ceil(data.length / itemsPerPage);

    const handleSort = (
        colKey: 'dueDate' | 'assignedDate' | 'hasNewMessage',
        direction: 'asc' | 'desc'
    ) => {
        setSortColumn(colKey);
        setSortDirection(direction);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const renderLoadingState = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={30} sx={{ mb: 2 }} />
            <Typography color={(t) => t.palette.action.active} variant="subtitle2">
                Loading reviews...
            </Typography>
        </Box>
    );

    const renderContent = () => {
        if (loading) {
            return renderLoadingState();
        }

        return (
            <ClinicalReviewListing
                onSort={handleSort}
                data={pagedData}
                paginationProps={{
                    count: pageCount,
                    page: page,
                    onChange: handlePageChange
                }}
                removeColumns={removeColumns}
            />
        );
    };

    return (
        <Card sx={cardStyle(isLargerThanSmall)}>
            <Card.Title>{title || 'Reviews'}</Card.Title>
            <Card.SubTitle>{subtitle || `${totalReviews} Assigned Reviews`}</Card.SubTitle>
            <Card.Body>{renderContent()}</Card.Body>
        </Card>
    );
};

export default ReviewsList;

const cardStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { width: '100%' };
    const smallerThanSm: SxProps<Theme> = {
        borderRadius: 0
    };

    return media ? biggerThanSm : { ...biggerThanSm, ...smallerThanSm };
};
